'use client';

import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { type ProductPriceSource } from '@eliadmin/core/src/utils/product-price-utils';
import { getNtProductPriceDisplay } from '@eliadmin/core/src/utils/nt/product-price';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * The core price resolution for the current customer (wholesale, price without VAT first); product
 * cards and the header search results.
 */
export function useProductPriceDisplay(product: ProductPriceSource) {
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const priceWithoutVatFirst = useGlobalStore(
    state => state.templateSettings.product_detail?.price_without_vat_first?.enabled,
  );
  return getNtProductPriceDisplay(product, { isWholesaler, priceWithoutVatFirst });
}

/**
 * Figma product card price row: the current price in Body/L Bold (text/danger when discounted) and
 * the price before discount struck through, in the currency format of the store.
 */
export function ProductPrice({
  product,
  className,
}: {
  product: ProductDisplayData;
  className?: string;
}) {
  const { t } = useTranslations();
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const showSecondary = useGlobalStore(
    state => state.templateSettings.product_teaser?.price_without_vat?.enabled,
  );
  const price = useProductPriceDisplay(product);
  if (price.priceOnRequest) return null;
  if (price.showMocVocPrices)
    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <p className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="nt-body-l-bold whitespace-nowrap text-[#0D110D]">
            VOC: {formatPrice(price.vocPrice, currency, currencySettings)}
          </span>
          <span className="nt-body-xs text-[#5D655D]">
            {price.secondaryIncludesVat ? t('text443') : t('text1122')}
          </span>
        </p>
        {showSecondary ? (
          <p className="nt-body-s text-[#5D655D]">
            {formatPrice(price.vocSecondaryPrice, currency, currencySettings)}{' '}
            {price.secondaryIncludesVat ? t('text1122') : t('text443')}
          </p>
        ) : null}
      </div>
    );

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <p className="flex flex-wrap items-center gap-x-3">
        <span
          className={cn(
            'nt-body-l-bold whitespace-nowrap',
            price.showPreviousPrice ? 'text-[#B42318]' : 'text-[#0D110D]',
          )}
        >
          {formatPrice(price.current, currency, currencySettings)}
        </span>
        {price.showPreviousPrice ? (
          <del className="nt-body-s whitespace-nowrap text-[#5D655D]">
            {formatPrice(price.beforeDiscount, currency, currencySettings)}
          </del>
        ) : null}
      </p>
      {showSecondary ? (
        <p className="nt-body-s text-[#5D655D]">
          {formatPrice(price.secondary, currency, currencySettings)}{' '}
          {price.secondaryIncludesVat ? t('text1122') : t('text443')}
        </p>
      ) : null}
    </div>
  );
}

/** Both price tiers use the same VAT basis; no strike-through or sale styling. */
export function WholesalePrices({
  price,
  detail = false,
  showSecondary = false,
  className,
}: {
  price: ReturnType<typeof getNtProductPriceDisplay>;
  detail?: boolean;
  showSecondary?: boolean;
  className?: string;
}) {
  const { t } = useTranslations();
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const format = (value: number | undefined) => formatPrice(value, currency, currencySettings);
  const includesVat = detail || !price.secondaryIncludesVat;
  const vatLabel = includesVat ? t('text1122') : t('text443');
  const secondaryLabel = includesVat ? t('text443') : t('text1122');
  const rows = [
    {
      label: 'MOC:',
      value: detail ? price.catalogRetailWithVat : price.mocPrice,
      secondary: detail ? price.catalogRetailWithoutVat : price.mocSecondaryPrice,
    },
    {
      label: 'VOC:',
      value: detail ? price.currentWithVat : price.vocPrice,
      secondary: detail ? price.currentWithoutVat : price.vocSecondaryPrice,
    },
  ];
  return (
    <div
      className={cn(
        'flex flex-col',
        detail ? 'gap-1' : 'gap-2',
        !detail && 'w-full rounded-xl border border-[#DCEBDD] bg-[#F3F8F3] p-3',
        className,
      )}
    >
      {rows.map((row, index) => (
        <div key={row.label} className={detail ? 'flex flex-col gap-1' : undefined}>
          <p
            className={cn(
              'flex flex-wrap items-baseline gap-x-2 gap-y-0.5',
              !detail && 'justify-between',
              index ? 'font-bold text-[#137D28]' : 'text-[#5D655D]',
              detail
                ? index
                  ? 'nt-price-l'
                  : 'nt-body-l'
                : index
                  ? 'nt-body-l-bold'
                  : 'nt-body-s',
            )}
          >
            <span>{row.label}</span>
            <span className="flex flex-wrap items-baseline justify-end gap-x-1">
              <span className="whitespace-nowrap">{format(row.value)}</span>
              <span className="nt-body-xs font-normal">{vatLabel}</span>
            </span>
          </p>
          {showSecondary && (!detail || index === 1) ? (
            <p className={cn('text-[#5D655D]', detail ? 'nt-body-s' : 'nt-body-xs')}>
              {format(row.secondary)} {secondaryLabel}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}
